import * as convert from 'convert-units';

import type Order from '~/types/Order';
import type Rate from '~/types/Rate';

import * as carriers from '~/constants/carriers';
import {
  LARGE_UPS_GIRTH,
  LARGE_UPS_LENGTH,
  LARGE_UPS_SURCHARGE_USD,
  LARGE_UPS_THRESHOLD,
  LARGE_UPS_WEIGHT,
  UPS_SURCHARGE_WARNING_URL,
} from '~/constants/carriers';
import {
  DEFAULT_CUSTOMS_ITEM_DESCRIPTION_MAX_LENGTH,
  USPS_CUSTOMS_ITEM_DESCRIPTION_MAX_LENGTH,
} from '~/constants/customsItem';
import { DistanceUnit, distanceUnitMap } from '~/constants/distance';
import { WeightUnit, safeWeightUnitMap } from '~/constants/weight';
import { type EELPFCValidation } from '~/modules/eelPfc/types/eelPfc.type';
import { isEELPFCRequiredForOrderShipmentAndRate } from '~/modules/eelPfc/utils/eelPfc.util';
import { type OrderShipmentV2, type OrderV2 } from '~/modules/orders/types/ordersV2.type';
import { getAddressesTypeSafe } from '~/types/Address';
import { getCustomsPhoneErrors, type CustomsValidationAddress } from '~/types/CustomsDeclaration';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { Events, track } from '~/utils/segment';

import {
  CUSTOMS_DESCRIPTION_LENGTH_WARNING,
  CUSTOMS_EEL_PFC_WARNING,
  CUSTOMS_PHONE_WARNING,
  UPS_LARGE_PACKAGE_SURCHARGE,
} from './RateWarningConstants';
import { type Warning } from './RateWarningTypes';
import { sortDimension } from './util';

const isPackageAboutToExceedUPSGirth = (
  parcel: OrderShipmentForRateValidation['parcel'],
  rateProvider?: string,
) => {
  if (!rateProvider || rateProvider !== carriers.UPS.name) return false;

  const sorted = sortDimension(
    parcel?.dimensions.length || '0',
    parcel?.dimensions.width || '0',
    parcel?.dimensions.height || '0',
  );

  const distanceUnit = parcel?.dimensions.unit || DistanceUnit.IN;
  const length = parseFloat(convert(sorted.length).from(distanceUnit).to(DistanceUnit.IN));
  const width = parseFloat(convert(sorted.width).from(distanceUnit).to(DistanceUnit.IN));
  const height = parseFloat(convert(sorted.height).from(distanceUnit).to(DistanceUnit.IN));

  // max girth calc =  is l + 2W + 2H
  const girth = length + 2 * width + 2 * height;

  return girth >= LARGE_UPS_GIRTH * LARGE_UPS_THRESHOLD;
};

const isPackageAboutToExceedUPSWeight = (
  parcel: OrderShipmentForRateValidation['parcel'],
  rateProvider?: string,
) => {
  if (!rateProvider || rateProvider !== carriers.UPS.name) return false;

  const weightUnit = parcel?.weight.unit || WeightUnit.LB;
  const weight = parseFloat(
    convert(parcel?.weight.value)
      .from(weightUnit)
      .to(WeightUnit.LB),
  );

  return weight >= LARGE_UPS_WEIGHT * LARGE_UPS_THRESHOLD;
};

const isPackageAboutToExceedUPSLength = (
  parcel: OrderShipmentForRateValidation['parcel'],
  rateProvider?: string,
) => {
  if (!rateProvider || rateProvider !== carriers.UPS.name) return false;

  const sorted = sortDimension(
    parcel?.dimensions.length || '0',
    parcel?.dimensions.width || '0',
    parcel?.dimensions.height || '0',
  );

  const distanceUnit = parcel?.dimensions.unit || DistanceUnit.IN;
  const length = parseFloat(convert(sorted.length).from(distanceUnit).to(DistanceUnit.IN));

  return length >= LARGE_UPS_LENGTH * LARGE_UPS_THRESHOLD;
};

export const hasCustomsDescriptionLengthWarning = (
  customsItems: { description: string }[] = [],
  selectedRateProvider: string | undefined,
): boolean => {
  if (selectedRateProvider !== carriers.USPS.name) return false;

  return customsItems.some(
    (item) => item.description.length > USPS_CUSTOMS_ITEM_DESCRIPTION_MAX_LENGTH,
  );
};

const trackWarning = (warning: Warning, order_id: string): void => {
  const { kind, message } = warning;
  track(Events.ups_surcharge_warning, {
    kind,
    message,
    order_id,
  });
};

export const getRateWarningsV1 = (
  order: Order,
  batchEntry?: BatchEntryExtended | null,
  rate?: Rate | null,
  aesItnFlag?: boolean,
): Warning[] => {
  const { addressFrom, addressTo } = getAddressesTypeSafe(order, batchEntry);

  return getRateWarnings({
    aesItnFlag: aesItnFlag || false,
    order: { object_id: order.objectId },
    orderShipment: {
      address_from: addressFrom,
      address_to: addressTo,
      customs: batchEntry?.customsDeclaration || null,
      customs_items: batchEntry?.customsItems || [],
      parcel: batchEntry?.parcel
        ? {
            dimensions: {
              height: batchEntry.parcel.height,
              length: batchEntry.parcel.length,
              unit: distanceUnitMap[batchEntry.parcel.distanceUnit],
              width: batchEntry.parcel.width,
            },
            weight: {
              unit: safeWeightUnitMap[batchEntry.parcel.weightUnit],
              value: batchEntry.parcel.weight,
            },
          }
        : null,
    },
    rate,
  });
};

type OrderShipmentForRateValidation = {
  address_from?: CustomsValidationAddress | null;
  address_to?: CustomsValidationAddress | null;
  customs: EELPFCValidation | null;
  customs_items: Pick<OrderShipmentV2['customs_items'][number], 'description'>[];
  parcel: Pick<NonNullable<OrderShipmentV2['parcel']>, 'dimensions' | 'weight'> | null;
};

export const getRateWarnings = ({
  aesItnFlag,
  order,
  orderShipment,
  rate,
}: {
  aesItnFlag: boolean;
  order: Pick<OrderV2, 'object_id'>;
  orderShipment: OrderShipmentForRateValidation;
  rate?: Pick<Rate, 'provider'> | null;
}) => {
  const orderObjectId = order.object_id;
  const {
    address_from: addressFrom,
    address_to: addressTo,
    customs: customsDeclaration,
    customs_items: customsItems,
    parcel,
  } = orderShipment;
  const rateProvider = rate?.provider;

  const warnings: Warning[] = [];

  // UPS warning section
  let warning: Warning;

  const upsSurchargeAmount = {
    currency_iso: 'USD',
    minimumFractionDigits: LARGE_UPS_SURCHARGE_USD % 1 === 0 ? 0 : 2,
    value: LARGE_UPS_SURCHARGE_USD,
  };

  if (
    isPackageAboutToExceedUPSWeight(parcel, rateProvider) ||
    isPackageAboutToExceedUPSGirth(parcel, rateProvider) ||
    isPackageAboutToExceedUPSLength(parcel, rateProvider)
  ) {
    warning = {
      kind: UPS_LARGE_PACKAGE_SURCHARGE,
      linkCopy: 'orders:table.errors.learnMore',
      linkURL: UPS_SURCHARGE_WARNING_URL,
      message: 'orders:table.errors.packageAboutToExceedUPSLimitsWarning',
      messageArgs: {
        amount: upsSurchargeAmount,
      },
    };
    warnings.push(warning);
    trackWarning(warning, orderObjectId);
  }

  const { hasPhoneNumberRequiredWarning } = getCustomsPhoneErrors({
    addressFrom,
    addressTo,
  });

  if (hasPhoneNumberRequiredWarning) {
    warnings.push({
      kind: CUSTOMS_PHONE_WARNING,
      linkCopy: 'orders:calculateRates.tabs.shipmentInfo.recipientAddressEdit',
      message: 'orders:table.errors.ratesCustomsPhoneWarning',
    });
  }

  const hasEELPFCWarning = isEELPFCRequiredForOrderShipmentAndRate(
    { addressFrom, addressTo, customsDeclaration },
    rateProvider,
    aesItnFlag,
  );
  if (hasEELPFCWarning) {
    warnings.push({
      kind: CUSTOMS_EEL_PFC_WARNING,
      linkCopy: 'orders:eelPfcSection.warningLink',
      message: 'orders:eelPfcSection.warningMessage',
    });
  }

  if (hasCustomsDescriptionLengthWarning(customsItems, rateProvider)) {
    warnings.push({
      kind: CUSTOMS_DESCRIPTION_LENGTH_WARNING,
      linkCopy: 'orders:customsDescriptionLengthWarning.link',
      linkURL:
        'https://support.goshippo.com/hc/en-us/articles/203004889-Understanding-Customs-Declarations#h_1fae898e-2d81-4644-801c-a5d85236663f',
      message: 'orders:customsDescriptionLengthWarning.message',
      messageArgs: {
        maxLength:
          rate?.provider === 'USPS'
            ? `${USPS_CUSTOMS_ITEM_DESCRIPTION_MAX_LENGTH}`
            : `${DEFAULT_CUSTOMS_ITEM_DESCRIPTION_MAX_LENGTH}`,
      },
    });
  }

  return warnings;
};
